'use client';

import { useState } from 'react';
import Link from 'next/link';

interface HomepageFAQProps {
  className?: string;
}

const faqs = [
  {
    question: 'When is the best time to dive in Cyprus?',
    answer:
      'Cyprus offers year-round diving, but the best conditions are from May to October. September and October are often considered the peak months — water temperatures reach 26–28°C, visibility is 25–30m, and there are fewer tourists than in peak summer. Spring (April–May) is also excellent for calm seas and active marine life.',
  },
  {
    question: 'Is the Zenobia wreck suitable for beginners?',
    answer:
      'The Zenobia is a 178m ferry lying on its side in Larnaca Bay at depths of 16–42m. The upper sections (16–24m) are accessible to Open Water divers with a guide, making it suitable for confident beginners. The deeper sections and penetration dives require Advanced Open Water certification or higher. Most dive centres in Larnaca offer guided Zenobia dives for all levels.',
  },
  {
    question: 'Do I need a dive computer to dive in Cyprus?',
    answer:
      'Most dive centres in Cyprus will provide or rent a dive computer if you do not own one. If you are diving independently, a dive computer is strongly recommended — particularly for multi-level dives like the Zenobia. Cyprus follows standard PADI/SSI certification requirements, and dive centres will check your certification card before any dive.',
  },
  {
    question: 'What marine life can I expect to see in Cyprus?',
    answer:
      'Cyprus waters are home to a wide range of Mediterranean species. Common sightings include dusky groupers, moray eels, octopus, cuttlefish, sea bream, and barracuda. Green and loggerhead sea turtles are regularly seen, particularly around Akamas and Larnaca. Lionfish (an invasive species) are also present. Seasonal visitors include rays, tuna, and occasionally hammerhead sharks in deeper water.',
  },
  {
    question: 'Which region in Cyprus is best for diving?',
    answer:
      'Each region offers something different. Larnaca is home to the Zenobia wreck — one of the world\'s top wreck dives. Paphos has the most dive centres and a variety of reef, cave, and shore dives. Ayia Napa and Protaras offer clear water, sea caves, and the MUSAN underwater sculpture museum. Limassol has Jubilee Shoals and wall dives. Akamas and Latchi are best for remote, uncrowded reefs and turtle sightings.',
  },
];

const HomepageFAQ = ({ className = '' }: HomepageFAQProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section
      className={`py-12 sm:py-14 md:py-16 ${className}`}
      style={{
        background: '#0B1622',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <p
              className="text-xs font-semibold uppercase tracking-widest mb-2"
              style={{ color: '#FF8A3D' }}
            >
              Common Questions
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-headline font-bold mb-3"
              style={{ color: '#EAF4F8' }}
            >
              Frequently Asked Questions
            </h2>
            <p
              className="text-sm sm:text-base max-w-xl mx-auto"
              style={{ color: '#C8DDE6' }}
            >
              Practical answers to the questions divers ask most before visiting Cyprus.
            </p>
          </div>

          {/* Accordion */}
          <div className="space-y-3">
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden transition-all duration-200"
                  style={{
                    background: '#0F2234',
                    border: isOpen
                      ? '1px solid rgba(111,211,224,0.30)'
                      : '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                    aria-expanded={isOpen}
                  >
                    <span
                      className="font-headline font-semibold text-sm sm:text-base leading-snug"
                      style={{ color: '#EAF4F8' }}
                    >
                      {faq.question}
                    </span>
                    <span
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200"
                      style={{
                        background: isOpen
                          ? 'rgba(111,211,224,0.15)'
                          : 'rgba(255,255,255,0.06)',
                        transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={isOpen ? '#6FD3E0' : '#6B8FA0'}
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-3.5 h-3.5"
                        aria-hidden="true"
                      >
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </span>
                  </button>

                  {isOpen && (
                    <div
                      className="px-5 pb-5"
                      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <p
                        className="text-sm sm:text-base leading-relaxed pt-4"
                        style={{ color: '#C8DDE6' }}
                      >
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer CTA */}
          <div className="mt-8 text-center">
            <p className="text-sm mb-3" style={{ color: '#6B8FA0' }}>
              More questions?
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
              style={{ color: '#6FD3E0' }}
            >
              Ask us directly →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomepageFAQ;
